import { t } from 'i18next';
import * as XlsxPopulate from 'xlsx-populate/browser/xlsx-populate';
// tslint:disable-next-line:no-any
(window as any).IQc = { entity: 'x', ENTITIES: { x: 'x'} };

import { UIHelper } from './UIHelper';
import { Languages, LanguageSetting } from '../constants/Languages';
import { DownloadHelper } from './DownloadHelper';
import { ArticleListMaker } from './ArticleListMaker';
import { ArticleItem } from '../stores/ArticleStore';
import { ProjectSettingsStore } from '../stores/ProjectSettingsStore';
import { PersistentStore } from '../stores/PersistentStore';

import utils = egr.wcf.utils;
import eaiws = egr.wcf.eaiws;

class XLSXEntry {
    public articleInformation: string;
    public projectName: string;
    public manufacturer: string;
    public description: string;
    public quantity: number = 1;
    public singlePrice: number;

    constructor(articleData: eaiws.basket.ItemProperties, prices: boolean) {
        this.articleInformation = ArticleListMaker.getArticleHeader(articleData);
        this.manufacturer = ArticleListMaker.getManufacturerHeader(articleData);
        let description: Array<string> = [];
        if (articleData.article != null) {
            if (!utils.string.isNullOrEmpty(articleData.article.longText)) {
                description.push(articleData.article!.longText + '\n');
            }
            if (!utils.string.isNullOrEmpty(articleData.article.featureText)) {
                description = description.concat(UIHelper.multilineTextToArray(articleData.article.featureText));
            }
        }
        this.description = description.join('\n');
        this.singlePrice = ArticleListMaker.getPrice(articleData, false);
    }
}

class XLSXTemplateInfo {
    public pageHeader: {row: number, col: number};
    public projectName: {row: number, col: number};
    public row: number = 0;
    public position: number;
    public articleInformation: number;
    public manufacturer: number;
    public description: number;
    public quantity: number;
    public singlePrice: number;
    public totalPrice: number;

    public rowHeader: number = 0;
    public positionHeader: number;
    public articleInformationHeader: number;
    public manufacturerHeader: number;
    public descriptionHeader: number;
    public quantityHeader: number;
    public singlePriceHeader: number;
    public totalPriceHeader: number;

    public fontSize: number;
    public fontFamily: string;

    constructor(templateSheet: any) {
        for (let row: number = 1; row < 20; row++) {
            for (let col: number = 1; col < 20; col++) {
                switch (templateSheet.row(row).cell(col).value() as string) {
                    case '{PAGEHEADER}':
                        this.pageHeader = {row: row, col: col};
                        break;
                    case '{PROJECTNAME}':
                        this.projectName = {row: row, col: col};
                        break;
                    case '{POS}':
                        this.position = col;
                        this.row = row;
                        break;
                    case '{ARTICLEINFO}':
                        this.articleInformation = col;
                        this.row = row;
                        templateSheet.row(row).cell(col).value('');
                        break;
                    case '{MANUFACTURER}':
                        this.manufacturer = col;
                        this.row = row;
                        templateSheet.row(row).cell(col).value('');
                        break;
                    case '{DESCRIPTION}':
                        this.description = col;
                        this.row = row;
                        this.fontFamily = templateSheet.row(row).cell(col).style('fontFamily');
                        this.fontSize = templateSheet.row(row).cell(col).style('fontSize');
                        templateSheet.row(row).cell(col).value('');
                        break;
                    case '{COUNT}':
                        this.quantity = col;
                        this.row = row;
                        break;
                    case '{PRICESINGLE}':
                        this.singlePrice = col;
                        this.row = row;
                        break;
                    case '{PRICETOTAL}':
                        this.totalPrice = col;
                        this.row = row;
                        break;
                    case '{POS-HEADER}':
                        this.positionHeader = col;
                        this.rowHeader = row;
                        break;
                    case '{ARTICLEINFO-HEADER}':
                        this.articleInformationHeader = col;
                        this.rowHeader = row;
                        break;
                    case '{MANUFACTURER-HEADER}':
                        this.manufacturerHeader = col;
                        this.rowHeader = row;
                        break;
                    case '{DESCRIPTION-HEADER}':
                        this.descriptionHeader = col;
                        this.rowHeader = row;
                        break;
                    case '{COUNT-HEADER}':
                        this.quantityHeader = col;
                        this.rowHeader = row;
                        break;
                    case '{PRICESINGLE-HEADER}':
                        this.singlePriceHeader = col;
                        this.rowHeader = row;
                        break;
                    case '{PRICETOTAL-HEADER}':
                        this.totalPriceHeader = col;
                        this.rowHeader = row;
                        break;
                    default:
                        break;
                }
            }
        }
    }
}

export class XLSXDocument {
    protected contentList: Array<XLSXEntry> = [];

    constructor(articleList: Array<ArticleItem>) {
        for (const article of articleList) {
            this.contentList.push(new XLSXEntry(article.properties, UIHelper.showPrices()));
        }
    }

    public async downloadDocument(showPrices: boolean, filename: string = 'out.xlsx'): Promise<void> {
        return this.getDocument(showPrices).then((workbook: any): void => {
            workbook.outputAsync().then((blob: Blob) => {
                DownloadHelper.downloadBlob(blob, filename);
            });
        });
    }

    public async getDocument(showPrices: boolean): Promise<Blob | string> {
        const projectName: string = ArticleListMaker.getArticleListHeader();
        const currencyFormat: string = this.getCurrencyFormat();
        return DownloadHelper.getContent('./res/template.xlsx').then((result) => {
            return XlsxPopulate.fromDataAsync(result).then((workbook: any) => {
                const articleList: any = workbook.sheet(0);
                const templateInfo: XLSXTemplateInfo = new XLSXTemplateInfo(articleList);
                if (templateInfo.rowHeader != null) {
                    if (templateInfo.positionHeader != null) {
                        articleList.row(templateInfo.rowHeader).cell(templateInfo.positionHeader).value(t('Pos.'));
                    }
                    if (templateInfo.articleInformationHeader != null) {
                        articleList.row(templateInfo.rowHeader).cell(templateInfo.articleInformationHeader).value(t('Article'));
                    }
                    if (templateInfo.manufacturerHeader != null) {
                        articleList.row(templateInfo.rowHeader).cell(templateInfo.manufacturerHeader).value(t('Manufacturer'));
                    }
                    if (templateInfo.descriptionHeader != null) {
                        articleList.row(templateInfo.rowHeader).cell(templateInfo.descriptionHeader).value(t('Description'));
                    }
                    if (templateInfo.quantityHeader != null) {
                        articleList.row(templateInfo.rowHeader).cell(templateInfo.quantityHeader).value(t('Quantity'));
                    }
                    if (templateInfo.singlePriceHeader != null) {
                        if (showPrices) {
                            articleList.row(templateInfo.rowHeader).cell(templateInfo.singlePriceHeader).value(t('Single price'));
                        } else {
                            articleList.row(templateInfo.rowHeader).cell(templateInfo.singlePriceHeader)
                                .value('').style('border', false);
                        }
                    }
                    if (templateInfo.totalPriceHeader != null) {
                        if (showPrices) {
                            articleList.row(templateInfo.rowHeader).cell(templateInfo.totalPriceHeader).value(t('Total price'));
                        } else {
                            articleList.row(templateInfo.rowHeader).cell(templateInfo.totalPriceHeader)
                                .value('').style('border', false);
                        }
                    }
                }
                if (templateInfo.projectName != null) {
                    articleList.row(templateInfo.projectName.row).cell(templateInfo.projectName.col)
                        .value(utils.string.isNullOrEmpty(projectName) ? '' : projectName);
                }
                if (templateInfo.pageHeader != null) {
                    articleList.row(templateInfo.pageHeader.row).cell(templateInfo.pageHeader.col)
                        .value(t('Article List'));
                }
                const startRow: number = templateInfo.row;
                this.contentList.map((entry, index) => {
                    let linesAdded: number = 0;
                    if (templateInfo.position != null) {
                        articleList.row(templateInfo.row).cell(templateInfo.position)
                            .value(index + 1)
                            .style('verticalAlignment', 'top')
                            .style('wrapText', true);
                        linesAdded = Math.max(linesAdded, 1);
                    }
                    if (templateInfo.articleInformation != null && entry.articleInformation) {
                        articleList.row(templateInfo.row).cell(templateInfo.articleInformation)
                            .value(entry.articleInformation)
                            .style('verticalAlignment', 'top')
                            .style('wrapText', true);
                        linesAdded = Math.max(linesAdded, 1);
                    }
                    if (templateInfo.manufacturer != null && entry.manufacturer) {
                        articleList.row(templateInfo.row).cell(templateInfo.manufacturer)
                            .value(entry.manufacturer)
                            .style('verticalAlignment', 'top')
                            .style('wrapText', true);
                        linesAdded = Math.max(linesAdded, 1);
                    }
                    if (templateInfo.description != null && entry.description) {
                        articleList.row(templateInfo.row).cell(templateInfo.description)
                            .value(entry.description)
                            .style('verticalAlignment', 'top')
                            .style('wrapText', true);
                        linesAdded = Math.max(linesAdded, 1);
                    }
                    if (templateInfo.quantity != null && entry.quantity) {
                        articleList.row(templateInfo.row).cell(templateInfo.quantity)
                            .value(entry.quantity)
                            .style('verticalAlignment', 'top')
                            .style('wrapText', true);
                        linesAdded = Math.max(linesAdded, 1);
                    }
                    if (templateInfo.singlePrice != null) {
                        if (entry.singlePrice) {
                            articleList.row(templateInfo.row).cell(templateInfo.singlePrice)
                                .value(entry.singlePrice)
                                .style('verticalAlignment', 'top')
                                .style('wrapText', true)
                                .style('numberFormat', currencyFormat);
                            linesAdded = Math.max(linesAdded, 1);
                        } else {
                            articleList.row(templateInfo.row).cell(templateInfo.singlePrice).value('');
                            linesAdded = Math.max(linesAdded, 1);
                        }
                    }
                    if (templateInfo.totalPrice != null) {
                        if (entry.quantity && entry.singlePrice) {
                            const columnNameQuantity: string = articleList.row(templateInfo.row).cell(templateInfo.quantity).columnName();
                            const columnNameSinglePrice: string = articleList.row(templateInfo.row).cell(templateInfo.singlePrice).columnName();
                            articleList.row(templateInfo.row).cell(templateInfo.totalPrice)
                                .formula(
                                    columnNameQuantity + (templateInfo.row).toString() + '*' +
                                    columnNameSinglePrice + (templateInfo.row).toString()
                                )
                                .style('verticalAlignment', 'top')
                                .style('wrapText', true)
                                .style('numberFormat', currencyFormat);
                            linesAdded = Math.max(linesAdded, 1);
                        } else {
                            articleList.row(templateInfo.row).cell(templateInfo.totalPrice).value('');
                            linesAdded = Math.max(linesAdded, 1);
                        }
                    }
                    templateInfo.row += linesAdded;
                });

                if (templateInfo.position != null) {
                    articleList.row(templateInfo.row).cell(templateInfo.position)
                        .value(t('Total', {context: 'label of final row in an (excel) table'}))
                        .style('verticalAlignment', 'top')
                        .style('wrapText', true);
                    if (templateInfo.fontFamily && templateInfo.fontSize) {
                        articleList.row(templateInfo.row).cell(templateInfo.position)
                            .style('fontSize', templateInfo.fontSize)
                            .style('fontFamily', templateInfo.fontFamily);
                    }
                }

                if (templateInfo.description != null && showPrices) {
                    articleList.row(templateInfo.row).cell(templateInfo.description)
                        .value(t('Price (excl. Tax)'))
                        .style('verticalAlignment', 'top')
                        .style('wrapText', true);
                    if (templateInfo.fontFamily && templateInfo.fontSize) {
                        articleList.row(templateInfo.row).cell(templateInfo.description)
                            .style('fontSize', templateInfo.fontSize)
                            .style('fontFamily', templateInfo.fontFamily);
                    }
                }

                // if (templateInfo.quantity != null) {
                //     let columnName: string = articleList.row(templateInfo.row).cell(templateInfo.quantity).columnName();
                //     articleList.row(templateInfo.row).cell(templateInfo.quantity)
                //         .formula(
                //             "SUM(" +
                //             columnName +
                //             startRow.toString() +
                //             ":" +
                //             columnName +
                //             (templateInfo.row - 1).toString() +
                //             ")"
                //         )
                //         .style("verticalAlignment", "top")
                //         .style("wrapText", true);
                // }

                if (templateInfo.totalPrice != null && showPrices) {
                    const columnName: string = articleList.row(templateInfo.row).cell(templateInfo.totalPrice).columnName();
                    articleList.row(templateInfo.row).cell(templateInfo.totalPrice)
                        .formula(
                            'SUM(' +
                            columnName +
                            startRow.toString() +
                            ':' +
                            columnName +
                            (templateInfo.row - 1).toString() +
                            ')'
                        )
                        .style('verticalAlignment', 'top')
                        .style('wrapText', true)
                        .style('numberFormat', currencyFormat);
                    }
                return workbook;
            });
        });
    }

    protected getCurrencyFormat(): string {
        const currency: string = ProjectSettingsStore.getState().currency;
        const language: string | null = PersistentStore.getState().language;
        let lcid: string = '407'; // lcid for german
        const selectedLanguageSetting: LanguageSetting = Languages.filter((languageInfo) => {
            return languageInfo.short === language;
        })[0];
        if (selectedLanguageSetting != null) {
            lcid = selectedLanguageSetting.lcid;
        }
        return '#,##0.00\ [$' + currency + '-' + lcid + '];[RED]\-#,##0.00\ [$' + currency + '-' + lcid + ']';
    }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/XLSXDocument.ts